import { createSlice } from "@reduxjs/toolkit";

import {
  getRevenueThunk,
  generateRevenueThunk,
  updateRevenueThunk,
  deleteRevenueThunk,
  exportRevenueThunk,
} from "state/action/revenue";

// ─── Helper: default async state ─────────────────────────────────────────────
const asyncState = () => ({
  data: null,
  isFulfilled: false,
  isPending: false,
  isRejected: false,
  err: null,
});

// ─── Helper: amortization schedule generator ─────────────────────────────────
/**
 * Menghasilkan daftar bulan antara dua tanggal ISO
 * Untuk pembayaran bulanan tanggal 1: jika startDate bukan tanggal 1,
 * maka bulan pertama dimulai dari bulan berikutnya.
 *
 * Contoh: startDate = 15 April 2026 -> bulan pertama = 1 Mei 2026
 *
 * @param {string} startDate
 * @param {string} endDate
 * @returns {Array<{year, month, label}>}
 */
export function generateMonthRange(startDate, endDate) {
  const months = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Buat cursor untuk iterasi
  const cur = new Date(start);

  // Jika tanggal mulai bukan tanggal 1, mulai dari bulan berikutnya
  if (start.getDate() !== 1) {
    // Set ke tanggal 1 bulan berikutnya
    cur.setMonth(cur.getMonth() + 1);
    cur.setDate(1);
  } else {
    // Jika sudah tanggal 1, mulai dari bulan yang sama
    cur.setDate(1);
  }

  // Set end date ke tanggal 1 untuk perbandingan
  end.setDate(1);

  while (cur <= end) {
    months.push({
      year: cur.getFullYear(),
      month: cur.getMonth() + 1,
      label: cur.toLocaleDateString("id-ID", {
        month: "short",
        year: "numeric",
      }),
      isoDate: new Date(cur).toISOString(),
    });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

/**
 * Hitung amortisasi biaya PERIODIK (bulanan)
 */
/**
 * Cari harga bulanan per cost_type dari price_subtotals sebuah unit
 * @param {object} unitData - satu objek unit dengan price_subtotals
 * @param {string} costType - e.g. "Rental Fee", "Service Charge"
 * @returns {number} subtotal bulanan
 */
function getUnitMonthlyAmount(unitData, costType) {
  const subtotals = unitData?.price_subtotals || [];
  const match = subtotals.find(
    (s) =>
      s.key === costType?.toLowerCase().replace(/\s+/g, "_") ||
      s.label?.toLowerCase() === costType?.toLowerCase(),
  );
  // Gunakan subtotal jika sudah dihitung, atau price * qty
  if (!match) return 0;
  if (match.subtotal && match.subtotal > 0) return match.subtotal;
  return (match.price || 0) * (match.qty || 0);
}

/**
 * Hitung amortisasi untuk Utility dengan payment_schema "fix"
 * @param {object} cost - cost object dengan details.payment_schema === "fix"
 * @param {Array} months - dari generateMonthRange
 * @returns {Array} schedule rows per bulan
 */
function calcUtilityFixed(cost, months) {
  // UMKM + isUsingMinimumBilling → pakai minimumBilling; selainnya pakai cost
  const fixedCost = parseFloat(cost.details?.cost || 0);

  const billingDay = parseInt(
    cost.details?.billing_date?.split("-")[2] || "1",
    10,
  );
  const dueDay = parseInt(cost.details?.due_date?.split("-")[2] || "15", 10);

  let accumulatedRev = 0;
  let currentDeferred = 0;

  return months.map((m, idx) => {
    const billingDate = new Date(m.isoDate);
    billingDate.setDate(billingDay);

    const dueDate = new Date(billingDate);
    dueDate.setDate(dueDay);

    const revenueDate = new Date(billingDate);
    revenueDate.setDate(revenueDate.getDate() + 30); // Revenue recognition 30 hari setelah billing

    const invoiceAmount = fixedCost;
    const cashIn = fixedCost;
    const revRecognized = fixedCost;

    currentDeferred += invoiceAmount;
    currentDeferred -= revRecognized;
    accumulatedRev += revRecognized;

    return {
      period: idx + 1,
      month: m.label,
      year: m.year,
      monthNum: m.month,
      cost_type: cost.cost_type,
      payment_type: "utility-fixed",
      payment_schema: "fix",
      billing_date: billingDate.toLocaleDateString("id-ID"),
      due_date: dueDate.toLocaleDateString("id-ID"),
      revenue_recognition_date: revenueDate.toLocaleDateString("id-ID"),
      invoice_amount: invoiceAmount,
      cash_in: cashIn,
      revenue_recognized: revRecognized,
      deferred_revenue: currentDeferred,
      accumulated_revenue: accumulatedRev,
      unit_breakdown: [
        {
          unit_id: null,
          unit_number: "Fixed Utility",
          area_sqm: 0,
          amount: fixedCost,
        },
      ],
    };
  });
}

/**
 * Placeholder schedule untuk utility FLEX — invoice/cash_in/revenue = 0,
 * diisi dari pembacaan meter aktual.
 */
function calcUtilityFlex(cost, months) {
  return months.map((m, idx) => ({
    period: idx + 1,
    month: m.label,
    year: m.year,
    monthNum: m.month,
    cost_type: cost.cost_type,
    payment_type: "utility-flex",
    payment_schema: "flex",
    billing_date: "",
    due_date: "",
    revenue_recognition_date: "",
    invoice_amount: 0,
    cash_in: 0,
    revenue_recognized: 0,
    deferred_revenue: 0,
    accumulated_revenue: 0,
    unit_breakdown: (cost.details?.units || []).map((u) => ({
      unit_id: u.unit_id,
      unit_number: u.unit_number,
      area_sqm: Number(u.area_sqm) || 0,
      amount: 0,
    })),
  }));
}

// Mapping cost_type → charge name di unit.rate_periods[].costs[]
const COST_TYPE_TO_CHARGE = {
  "Rental Fee": "Rental Fee",
  "Service Charge": "Service Charge",
  "Utility Electricity Charge": "Electricity",
  "Utility Water Charge": "Water",
};

/**
 * Hitung amortisasi biaya PERIODIK - gabungkan semua unit.
 * Primary path: property.unit[].rate_periods[].costs[] (UI model baru).
 * Secondary path: cost.details.units[].rate_periods[].rate_per_sqm (backend format lama).
 * Fallback: property.unit[].price_subtotals (legacy).
 */
function calcPeriodic(cost, months, units) {
  const costFrequency = (cost.periodic?.frequency || "monthly").toLowerCase();
  const billingDay = parseInt(
    costFrequency === "quarterly"
      ? cost.periodic?.quarterly?.date || "1"
      : cost.periodic?.monthly?.date || "1",
    10,
  );
  const dueOffset = cost.periodic?.billing_config?.due_offset_days || 14;
  const revenueOffset =
    cost.periodic?.billing_config?.revenue_recognition_offset_days || 30;
  const qOffset = (cost.periodic?.quarterly?.month_offset || 1) - 1;

  // Primary: unit.rate_periods[].costs[] (UI model)
  const hasUnitRatePeriods =
    Array.isArray(units) &&
    units.some(
      (u) =>
        Array.isArray(u.rate_periods) &&
        u.rate_periods.length > 0 &&
        u.rate_periods.some(
          (rp) =>
            Array.isArray(rp.costs) &&
            rp.costs.some((c) => c.charge && c.price !== "" && c.price != null),
        ),
    );

  // Secondary: cost.details.units[].rate_periods[].rate_per_sqm
  const detailUnits = cost.details?.units;
  const useRatePeriods =
    !hasUnitRatePeriods &&
    Array.isArray(detailUnits) &&
    detailUnits.length > 0 &&
    detailUnits.some(
      (u) => Array.isArray(u.rate_periods) && u.rate_periods.length > 0,
    );

  let accumulatedRev = 0;
  let currentDeferred = 0;

  return months.map((m, idx) => {
    const leaseYearIndex = Math.floor(idx / 12) + 1;
    const mDate = new Date(m.isoDate);

    let monthlyRevenue = 0;
    let unitBreakdown = [];

    if (hasUnitRatePeriods) {
      // ── Primary: rate dari unit.rate_periods[].costs[] (UI model) ──
      const chargeName =
        COST_TYPE_TO_CHARGE[cost.cost_type] || cost.cost_type;

      unitBreakdown = units.map((u) => {
        const area = Number(u.area_sqm) || 0;
        const activeRp =
          (u.rate_periods || []).find((rp) => {
            const s = rp.start_period ? new Date(rp.start_period) : null;
            const e = rp.end_period ? new Date(rp.end_period) : null;
            return s && e && mDate >= s && mDate < e;
          }) ||
          (u.rate_periods || []).at(-1) ||
          null;

        const costRow = (activeRp?.costs || []).find(
          (c) => c.charge === chargeName || c.charge === cost.cost_type,
        );
        const rate = Number(costRow?.price) || 0;
        const monthlyAmount = rate * area;

        let amount;
        if (costFrequency === "yearly") {
          amount = idx % 12 === 0 ? monthlyAmount * 12 : 0;
        } else if (costFrequency === "quarterly") {
          amount = idx % 3 === qOffset ? monthlyAmount * 3 : 0;
        } else {
          amount = monthlyAmount;
        }

        monthlyRevenue += monthlyAmount;

        return {
          unit_id: u.unit_id || u._id,
          unit_number: u.unit_number,
          area_sqm: area,
          rate_per_sqm: rate,
          amount,
        };
      });
    } else if (useRatePeriods) {
      // ── Secondary: rate_per_sqm × area_sqm dari cost.details.units[].rate_periods ──
      unitBreakdown = detailUnits.map((u) => {
        const area = Number(u.area_sqm) || 0;
        const activeRp =
          (u.rate_periods || []).find((rp) => {
            const s = rp.start_period ? new Date(rp.start_period) : null;
            const e = rp.end_period ? new Date(rp.end_period) : null;
            return s && e && mDate >= s && mDate < e;
          }) ||
          (u.rate_periods || []).at(-1) ||
          null;

        const rate = activeRp ? Number(activeRp.rate_per_sqm) || 0 : 0;
        const monthlyAmount = rate * area;

        let amount;
        if (costFrequency === "yearly") {
          amount = idx % 12 === 0 ? monthlyAmount * 12 : 0;
        } else if (costFrequency === "quarterly") {
          amount = idx % 3 === qOffset ? monthlyAmount * 3 : 0;
        } else {
          amount = monthlyAmount;
        }

        monthlyRevenue += monthlyAmount;

        return {
          unit_id: u.unit_id,
          unit_number: u.unit_number,
          area_sqm: area,
          rate_per_sqm: rate,
          amount,
        };
      });
    } else {
      // ── Legacy: price_subtotals pada property.unit[] ──
      monthlyRevenue = (units || []).reduce((sum, u) => {
        const subtotals = u?.price_subtotals || [];
        const matches = subtotals.filter(
          (s) =>
            s.key === cost.cost_type?.toLowerCase().replace(/\s+/g, "_") ||
            s.label?.toLowerCase() === cost.cost_type?.toLowerCase(),
        );
        if (matches.length === 0) return sum;

        let match = matches[0];
        if (matches.length > 1 || matches[0].year) {
          match =
            matches.find((s) => s.year === leaseYearIndex) ||
            matches[matches.length - 1];
        }

        const sFreq = (match.frequency || "").toLowerCase();
        let baseAmount =
          match.subtotal && match.subtotal > 0
            ? match.subtotal
            : (match.price || 0) * (match.qty || 0);

        if (sFreq === "yearly") baseAmount = baseAmount / 12;
        else if (sFreq === "quarterly") baseAmount = baseAmount / 3;
        else if (sFreq === "daily") baseAmount = baseAmount * 30;
        else if (sFreq === "weekly") baseAmount = baseAmount * 4;

        return sum + baseAmount;
      }, 0);

      unitBreakdown = (units || []).map((u) => {
        const subtotals = u?.price_subtotals || [];
        const matches = subtotals.filter(
          (s) =>
            s.key === cost.cost_type?.toLowerCase().replace(/\s+/g, "_") ||
            s.label?.toLowerCase() === cost.cost_type?.toLowerCase(),
        );
        let matchAmount = 0;
        if (matches.length > 0) {
          let match = matches[0];
          if (matches.length > 1 || matches[0].year) {
            match =
              matches.find((s) => s.year === leaseYearIndex) ||
              matches[matches.length - 1];
          }
          const sFreq = (match.frequency || "").toLowerCase();
          let baseAmount =
            match.subtotal && match.subtotal > 0
              ? match.subtotal
              : (match.price || 0) * (match.qty || 0);

          let normalizedMonthly = baseAmount;
          if (sFreq === "yearly") normalizedMonthly = baseAmount / 12;
          else if (sFreq === "quarterly") normalizedMonthly = baseAmount / 3;
          else if (sFreq === "daily") normalizedMonthly = baseAmount * 30;
          else if (sFreq === "weekly") normalizedMonthly = baseAmount * 4;

          if (costFrequency === "yearly") {
            matchAmount = idx % 12 === 0 ? normalizedMonthly * 12 : 0;
          } else if (costFrequency === "quarterly") {
            matchAmount = idx % 3 === qOffset ? normalizedMonthly * 3 : 0;
          } else {
            matchAmount = normalizedMonthly;
          }
        }
        return {
          unit_id: u._id,
          unit_number: u.unit_number,
          area_sqm: u.area_sqm,
          amount: matchAmount,
        };
      });
    }

    const billingDate = new Date(m.isoDate);
    billingDate.setDate(billingDay);
    const dueDate = new Date(billingDate);
    dueDate.setDate(dueDate.getDate() + dueOffset);
    const revenueDate = new Date(billingDate);
    revenueDate.setDate(revenueDate.getDate() + revenueOffset);

    let invoiceAmount = 0;
    let cashIn = 0;
    let revRecognized = 0;

    if (costFrequency === "yearly") {
      if (idx % 12 === 0) {
        invoiceAmount = monthlyRevenue * 12;
        cashIn = monthlyRevenue * 12;
        revRecognized = monthlyRevenue * 12;
      }
    } else if (costFrequency === "quarterly") {
      if (idx % 3 === qOffset) {
        invoiceAmount = monthlyRevenue * 3;
        cashIn = monthlyRevenue * 3;
        revRecognized = monthlyRevenue * 3;
      }
    } else {
      invoiceAmount = monthlyRevenue;
      cashIn = monthlyRevenue;
      revRecognized = monthlyRevenue;
    }

    currentDeferred += invoiceAmount;
    currentDeferred -= revRecognized;
    accumulatedRev += revRecognized;

    return {
      period: idx + 1,
      month: m.label,
      year: m.year,
      monthNum: m.month,
      cost_type: cost.cost_type,
      payment_type: "periodic",
      billing_date: billingDate.toLocaleDateString("id-ID"),
      due_date: dueDate.toLocaleDateString("id-ID"),
      revenue_recognition_date: revenueDate.toLocaleDateString("id-ID"),
      invoice_amount: invoiceAmount,
      cash_in: cashIn,
      revenue_recognized: revRecognized,
      deferred_revenue: currentDeferred,
      accumulated_revenue: accumulatedRev,
      unit_breakdown: unitBreakdown,
    };
  });
}

/**
 * Hitung amortisasi biaya NON-PERIODIK (Fit Out, dll.)
 */
function calcNonPeriodic(cost, months) {
  const monthMap = {};
  months.forEach((m, idx) => {
    monthMap[`${m.year}-${String(m.month).padStart(2, "0")}`] = idx;
  });

  const schedule = months.map((m, idx) => ({
    period: idx + 1,
    month: m.label,
    year: m.year,
    monthNum: m.month,
    cost_type: cost.cost_type,
    payment_type: "non-periodic",
    billing_date: "",
    due_date: "",
    revenue_recognition_date: "",
    invoice_amount: 0,
    cash_in: 0,
    revenue_recognized: 0,
    deferred_revenue: 0,
    accumulated_revenue: 0,
    unit_breakdown: [],
  }));

  (cost.nonPeriodic?.terms || []).forEach((term) => {
    const termDate = new Date(term.date);
    const key = `${termDate.getFullYear()}-${String(termDate.getMonth() + 1).padStart(2, "0")}`;
    const idx = monthMap[key];
    if (idx === undefined) return;

    const dueDate = new Date(termDate);
    dueDate.setDate(dueDate.getDate() + (term.due_offset_days || 0));
    const revenueDate = new Date(termDate);
    revenueDate.setDate(
      revenueDate.getDate() + (term.revenue_recognition_offset_days || 7),
    );

    const amount = parseFloat(term.amount) || 0;
    const revMonth = revenueDate.getMonth() + 1;
    const revYear = revenueDate.getFullYear();
    const diffMonth =
      revYear !== termDate.getFullYear() ||
      revMonth !== termDate.getMonth() + 1;

    schedule[idx].billing_date = termDate.toLocaleDateString("id-ID");
    schedule[idx].due_date = dueDate.toLocaleDateString("id-ID");
    schedule[idx].revenue_recognition_date =
      revenueDate.toLocaleDateString("id-ID");
    schedule[idx].invoice_amount += amount;
    schedule[idx].cash_in += amount;
    schedule[idx].unit_breakdown = [{ amount, notes: term.notes || "" }];

    if (diffMonth) {
      schedule[idx].deferred_revenue += amount;
      const revKey = `${revYear}-${String(revMonth).padStart(2, "0")}`;
      const revIdx = monthMap[revKey];
      if (revIdx !== undefined) {
        schedule[revIdx].revenue_recognized += amount;
        schedule[revIdx].deferred_revenue -= amount;
      }
    } else {
      schedule[idx].revenue_recognized += amount;
    }
  });

  let accumulated = 0;
  schedule.forEach((row) => {
    accumulated += row.revenue_recognized;
    row.accumulated_revenue = accumulated;
  });

  return schedule;
}

/**
 * Hitung jadwal pembayaran TAMBAH DAYA (one-time, satu kali di bulan billing_date)
 * Nilai total_tagihan sudah dihitung di state (biaya_penambahan + ppn).
 * @param {object} cost   - cost object dengan cost_type === "Tambah Daya"
 * @param {Array}  months - dari generateMonthRange
 * @returns {Array} schedule rows — satu bulan berisi tagihan, sisanya nol
 */
function calcTambahDaya(cost, months) {
  const totalTagihan = parseFloat(cost.details?.total_tagihan) || 0;
  const biayaPenambahan = parseFloat(cost.details?.biaya_penambahan) || 0;
  const ppn = parseFloat(cost.details?.ppn) || 0;

  // Tentukan bulan tagihan dari billing_date di state
  const billingRaw = cost.details?.billing_date || "";
  const billingDateObj = billingRaw ? new Date(billingRaw) : null;
  const billingKey = billingDateObj
    ? `${billingDateObj.getFullYear()}-${String(billingDateObj.getMonth() + 1).padStart(2, "0")}`
    : null;

  const dueRaw = cost.details?.due_date || "";
  const dueDateObj = dueRaw ? new Date(dueRaw) : null;

  let accumulated = 0;

  return months.map((m, idx) => {
    const mKey = `${m.year}-${String(m.month).padStart(2, "0")}`;
    const isPaymentMonth = billingKey ? mKey === billingKey : idx === 0;

    const invoiceAmount = isPaymentMonth ? totalTagihan : 0;
    const cashIn = isPaymentMonth ? totalTagihan : 0;
    const revRecognized = isPaymentMonth ? totalTagihan : 0;
    accumulated += revRecognized;

    return {
      period: idx + 1,
      month: m.label,
      year: m.year,
      monthNum: m.month,
      cost_type: cost.cost_type,
      payment_type: "one-time",
      billing_date:
        isPaymentMonth && billingDateObj
          ? billingDateObj.toLocaleDateString("id-ID")
          : "",
      due_date:
        isPaymentMonth && dueDateObj
          ? dueDateObj.toLocaleDateString("id-ID")
          : "",
      revenue_recognition_date:
        isPaymentMonth && billingDateObj
          ? billingDateObj.toLocaleDateString("id-ID")
          : "",
      invoice_amount: invoiceAmount,
      cash_in: cashIn,
      revenue_recognized: revRecognized,
      deferred_revenue: 0,
      accumulated_revenue: accumulated,
      unit_breakdown: isPaymentMonth
        ? [
            {
              unit_id: null,
              unit_number: "Tambah Daya",
              area_sqm: 0,
              amount: totalTagihan,
              biaya_penambahan: biayaPenambahan,
              ppn: ppn,
              daya_awal: cost.details?.daya_awal || "",
              daya_akhir: cost.details?.daya_akhir || "",
            },
          ]
        : [],
    };
  });
}

/**
 * Gabungkan semua schedule per bulan jadi satu consolidated row
 */
function consolidate(allSchedules, months) {
  return months.map((m, idx) => {
    const rows = allSchedules.map((s) => s[idx]).filter(Boolean);
    const row = {
      period: idx + 1,
      month: m.label,
      year: m.year,
      monthNum: m.month,
      breakdown: {},
      total_invoice: 0,
      total_cash_in: 0,
      total_revenue_recognized: 0,
      total_deferred: 0,
    };
    rows.forEach((r) => {
      if (!row.breakdown[r.cost_type]) {
        row.breakdown[r.cost_type] = {
          invoice_amount: 0,
          cash_in: 0,
          revenue_recognized: 0,
          deferred_revenue: 0,
          billing_date: r.billing_date,
          due_date: r.due_date,
          revenue_recognition_date: r.revenue_recognition_date,
          payment_type: r.payment_type,
          unit_breakdown: r.unit_breakdown || [],
        };
      } else {
        // Merge unit_breakdown jika cost_type sama dari schedule berbeda
        row.breakdown[r.cost_type].unit_breakdown = [
          ...row.breakdown[r.cost_type].unit_breakdown,
          ...(r.unit_breakdown || []),
        ];
      }
      row.breakdown[r.cost_type].invoice_amount += r.invoice_amount;
      row.breakdown[r.cost_type].cash_in += r.cash_in;
      row.breakdown[r.cost_type].revenue_recognized += r.revenue_recognized;
      row.breakdown[r.cost_type].deferred_revenue += r.deferred_revenue;
      row.total_invoice += r.invoice_amount;
      row.total_cash_in += r.cash_in;
      row.total_revenue_recognized += r.revenue_recognized;
      row.total_deferred += r.deferred_revenue;
    });
    return row;
  });
}

/**
 * ── REVENUE SHARE SCHEDULE ────────────────────────────────────────────────────
 * Menghasilkan invoice schedule untuk skema Bagi Hasil / Bagi Hasil dengan MGP.
 *
 * Setiap bulan dalam range kontrak akan menghasilkan SATU baris dengan:
 *   - invoice_amount = estimasi bagi hasil bulan tersebut (0 karena aktual dari sales recap)
 *   - billing_date / due_date sesuai config finansial
 *   - hasRevenueShare = true sebagai penanda untuk invoice generator
 *
 * Untuk skema "Bagi Hasil" (flat_rate / mgp / yearly_rates):
 *   - Gunakan `financials.details.yearly_rates[year-1].rate_percent` untuk estimasi
 *   - Nominal aktual baru diketahui saat sales recap dimasukkan
 *
 * @param {object} financials - property.financials ({ revenue_type, details })
 * @param {Array}  months     - dari generateMonthRange()
 * @param {string} startDate  - ISO date string awal kontrak (untuk hitung tahun ke-N)
 * @returns {Array} schedule rows per bulan
 */
function calcRevenueShare(financials, months, startDate) {
  const details = financials?.details || {};
  const revenueType = financials?.revenue_type || "";

  // Konfigurasi billing
  const billingDay = parseInt(details.monthly?.date || "1", 10);
  const dueOffset = parseInt(details.billing_config?.due_offset_days ?? 14, 10);
  const revenueOffset = parseInt(
    details.billing_config?.revenue_recognition_offset_days ?? 30,
    10,
  );
  const salesRecapDays = parseInt(
    details.billing_config?.sales_recapitulation_days ?? 0,
    10,
  );

  // Ambil yearly_rates untuk estimasi (jika ada)
  const yearlyRates = details.yearly_rates || [];

  const contractStart = new Date(startDate);

  return months.map((m, idx) => {
    // Hitung tahun kontrak yang berjalan (1-based)
    const monthDate = new Date(m.isoDate);
    const yearsDiff =
      (monthDate.getFullYear() - contractStart.getFullYear()) * 12 +
      (monthDate.getMonth() - contractStart.getMonth());
    const contractYear = Math.max(1, Math.floor(yearsDiff / 12) + 1);

    // Cari rate untuk tahun ini (fallback ke rate terakhir)
    const yearlyRate =
      yearlyRates.find((r) => r.year === contractYear) ||
      yearlyRates[yearlyRates.length - 1] ||
      null;

    // Estimasi invoice (placeholder 0 — aktual dari sales recap)
    // Untuk tampilan schedule, tunjukkan estimasi rate jika ada
    const estimatedRate = parseFloat(yearlyRate?.rate_percent) || 0;
    const mgpFloor = parseFloat(yearlyRate?.mgp_amount) || 0;

    const billingDate = new Date(m.isoDate);
    billingDate.setDate(billingDay);

    const salesRecapDate = new Date(billingDate);
    salesRecapDate.setDate(salesRecapDate.getDate() + salesRecapDays);

    const dueDate = new Date(billingDate);
    dueDate.setDate(dueDate.getDate() + dueOffset);

    const revenueDate = new Date(billingDate);
    revenueDate.setDate(revenueDate.getDate() + revenueOffset);

    return {
      period: idx + 1,
      month: m.label,
      year: m.year,
      monthNum: m.month,
      cost_type: "Revenue Share",
      payment_type: "revenue-share",
      revenue_type: revenueType,
      contract_year: contractYear,
      // Tanggal
      billing_date: billingDate.toLocaleDateString("id-ID"),
      sales_recap_date: salesRecapDate.toLocaleDateString("id-ID"),
      due_date: dueDate.toLocaleDateString("id-ID"),
      revenue_recognition_date: revenueDate.toLocaleDateString("id-ID"),
      // Nilai placeholder (aktual diisi saat sales recap)
      invoice_amount: 0,
      cash_in: 0,
      revenue_recognized: 0,
      deferred_revenue: 0,
      accumulated_revenue: 0,
      // Metadata untuk kalkulasi saat sales recap diinput
      rate_percent: estimatedRate,
      mgp_amount: mgpFloor,
      max_revenue_share_amount:
        parseFloat(yearlyRate?.max_revenue_share_amount) || 0,
      // Flag untuk invoice generator
      hasRevenueShare: true,
      unit_breakdown: [],
    };
  });
}

/**
 * ── PUBLIC PURE FUNCTION ──────────────────────────────────────────────────────
 * Kompute invoice & amortization schedule dari SATU serviceData item.
 * Cocok dipanggil langsung tanpa Redux (e.g. saat build payload API).
 *
 * Mendukung dua mode:
 *   1. Cost-based  — berdasarkan costs[] di property (sewa tetap, utility, dll)
 *   2. Revenue Share — jika property.financials.revenue_type adalah
 *      "Bagi Hasil" atau "Bagi Hasil dengan MGP", generate invoice schedule
 *      bulanan dengan placeholder 0 yang akan diisi dari sales recap
 *
 * @param {object} serviceData - Satu item dari array `services`
 * @returns {{
 *   amortizationSchedule: Array,
 *   costSchedules: Object,
 *   annualSummary: Object,
 *   summary: Object,
 *   months: Array,
 *   isRevenueShare: boolean,
 * }}
 */
export function generateInvoiceSchedule(serviceData) {
  const property = serviceData?.details?.properties?.[0];
  if (!property) return null;

  const startDate = property.start_date || property.handover_date;
  const endDate = property.end_property_date;
  if (!startDate || !endDate) return null;

  const months = generateMonthRange(startDate, endDate);
  const units = property.unit || [];
  const costs = property.costs || [];

  // ── Deteksi skema Bagi Hasil ────────────────────────────────────────────────
  const financials = property.financials || {};
  const revenueType = financials.revenue_type || "";
  const isRevenueShare =
    revenueType === "Bagi Hasil" || revenueType === "Bagi Hasil dengan MGP";

  const allSchedules = [];
  const costSchedules = {};

  // ── Mode Bagi Hasil ─────────────────────────────────────────────────────────
  if (isRevenueShare) {
    const revShareSchedule = calcRevenueShare(financials, months, startDate);
    if (revShareSchedule.length > 0) {
      costSchedules["Revenue Share"] = revShareSchedule;
      allSchedules.push(revShareSchedule);
    }
  }

  // ── Mode Cost-based (tetap dihitung, bisa berjalan bersamaan) ───────────────
  costs.forEach((cost) => {
    let schedule = [];

    const isUtilityElectricity =
      cost.cost_type === "Utility Electricity Charge";
    const isUtilityWater = cost.cost_type === "Utility Water Charge";
    const isTambahDaya = cost.cost_type === "Tambah Daya";
    const isFixedPayment = cost.details?.payment_schema === "Fix";

    if (isTambahDaya) {
      schedule = calcTambahDaya(cost, months);
    } else if (isUtilityElectricity || isUtilityWater) {
      // Fix → hitung amount tetap; Flex → tampilkan cost, invoice/cash/rev = 0
      if (isFixedPayment) schedule = calcUtilityFixed(cost, months);
      else schedule = calcUtilityFlex(cost, months);
    } else if (cost.paymentType === "periodic" && cost.periodic) {
      schedule = calcPeriodic(cost, months, units);
    } else if (cost.paymentType === "non-periodic" && cost.nonPeriodic) {
      schedule = calcNonPeriodic(cost, months);
    }

    if (schedule.length > 0) {
      costSchedules[cost.cost_type] = schedule;
      allSchedules.push(schedule);
    }
  });

  const amortizationSchedule = consolidate(allSchedules, months);
  const costTypes = Object.keys(costSchedules);

  // Annual summary
  const annualSummary = {};
  amortizationSchedule.forEach((row) => {
    const yr = row.year;
    if (!annualSummary[yr]) {
      annualSummary[yr] = {
        year: yr,
        total_invoice: 0,
        total_cash_in: 0,
        total_revenue_recognized: 0,
        total_deferred: 0,
      };
    }
    annualSummary[yr].total_invoice += row.total_invoice;
    annualSummary[yr].total_cash_in += row.total_cash_in;
    annualSummary[yr].total_revenue_recognized += row.total_revenue_recognized;
    annualSummary[yr].total_deferred += row.total_deferred;
  });

  const summary = {
    totalInvoice: amortizationSchedule.reduce((s, r) => s + r.total_invoice, 0),
    totalCashFlow: amortizationSchedule.reduce(
      (s, r) => s + r.total_cash_in,
      0,
    ),
    totalRevenue: amortizationSchedule.reduce(
      (s, r) => s + r.total_revenue_recognized,
      0,
    ),
    totalDeferred: amortizationSchedule.reduce(
      (s, r) => s + Math.abs(r.total_deferred),
      0,
    ),
    periodCount: months.length,
    unitCount: units.length,
    costTypes,
    startDate: months[0]?.label || "",
    endDate: months[months.length - 1]?.label || "",
    isRevenueShare,
    revenueType: isRevenueShare ? revenueType : null,
  };

  return {
    service_type: serviceData.service_type,
    amortizationSchedule,
    costSchedules,
    annualSummary,
    summary,
    months,
    isRevenueShare,
  };
}

// ─── Initial State ────────────────────────────────────────────────────────────
const initialState = {
  // Async states (GET)
  getRevenue: asyncState(),

  // Async states (GENERATE - compute dari service data lokal)
  generateRevenue: asyncState(),

  // Async states (UPDATE)
  updateRevenue: asyncState(),

  // Async states (DELETE)
  deleteRevenue: asyncState(),

  // Async states (EXPORT)
  exportRevenue: asyncState(),

  // Input body untuk generate
  generateRevenueInputBody: {
    service_type: "",
    property_id: null,
    building_id: null,
    floor_id: null,
    unit_id: null,
    start_date: null,
    end_date: null,
    costs: [],
  },

  // Hasil olahan schedule (computed lokal dari service data)
  amortizationSchedule: [], // consolidated per bulan
  costSchedules: {}, // { [cost_type]: ScheduleRow[] }
  months: [], // daftar bulan
  annualSummary: {}, // {[year]: { total_invoice, total_cash_in, ...}}

  // Summary card
  summary: {
    totalInvoice: 0,
    totalCashFlow: 0,
    totalRevenue: 0,
    totalDeferred: 0,
    periodCount: 0,
    unitCount: 0,
    costTypes: [],
    startDate: "",
    endDate: "",
  },

  // UI state
  selectedCostType: "all", // filter per cost type
  viewMode: "combined", // 'combined' | 'revenue' | 'cashflow'
  selectedSchedule: {}, // row yang sedang dilihat detail
};

// ─── Slice ────────────────────────────────────────────────────────────────────
export const revenueSlice = createSlice({
  name: "revenue",
  initialState,

  reducers: {
    // ── Input body field setter (mirip setRoleField) ──────────────────────────
    setRevenueField: (state, action) => {
      const { field, value } = action.payload;
      state.generateRevenueInputBody[field] = value;
    },

    // ── Set full input body sekaligus ─────────────────────────────────────────
    setGenerateRevenueInputBody: (state, action) => {
      return { ...state, generateRevenueInputBody: action.payload };
    },

    // ── Compute amortization schedule dari service data (lokal, tanpa API) ────
    computeSchedule: (state, action) => {
      /**
       * payload: serviceData object (satu item dari array API response)
       * Mendukung:
       *  - cost-based  (periodik, non-periodik, utility-fixed)
       *  - revenue share (Bagi Hasil / Bagi Hasil dengan MGP)
       */
      const serviceData = action.payload;
      const property = serviceData?.details?.properties?.[0];
      if (!property) return;

      const startDate = property.start_date || property.handover_date;
      const endDate = property.end_property_date;
      if (!startDate || !endDate) return;

      const months = generateMonthRange(startDate, endDate);

      const units = property.unit || [];
      const costs = property.costs || [];

      // ── Deteksi skema Bagi Hasil ──────────────────────────────────────────
      const financials = property.financials || {};
      const revenueType = financials.revenue_type || "";
      const isRevenueShare = revenueType === "Bagi Hasil";

      const allSchedules = [];
      const costSchedules = {};

      // ── Mode Bagi Hasil ───────────────────────────────────────────────────
      if (isRevenueShare) {
        const revShareSchedule = calcRevenueShare(
          financials,
          months,
          startDate,
        );
        if (revShareSchedule.length > 0) {
          costSchedules["Revenue Share"] = revShareSchedule;
          allSchedules.push(revShareSchedule);
        }
      }

      // ── Mode Cost-based ───────────────────────────────────────────────────
      costs.forEach((cost) => {
        let schedule = [];

        const isUtilityElectricity =
          cost.cost_type === "Utility Electricity Charge";
        const isUtilityWater = cost.cost_type === "Utility Water Charge";
        const isTambahDaya = cost.cost_type === "Tambah Daya";
        const isFixedPayment = cost.details?.payment_schema === "Fix";

        if (isTambahDaya) {
          schedule = calcTambahDaya(cost, months);
        } else if (isUtilityElectricity || isUtilityWater) {
          // Fix → hitung amount tetap; Flex → tampilkan cost, invoice/cash/rev = 0
          if (isFixedPayment) schedule = calcUtilityFixed(cost, months);
          else schedule = calcUtilityFlex(cost, months);
        } else if (cost.paymentType === "periodic" && cost.periodic) {
          schedule = calcPeriodic(cost, months, units);
        } else if (cost.paymentType === "non-periodic" && cost.nonPeriodic) {
          schedule = calcNonPeriodic(cost, months);
        }

        if (schedule.length > 0) {
          costSchedules[cost.cost_type] = schedule;
          allSchedules.push(schedule);
        }
      });

      const consolidated = consolidate(allSchedules, months);
      const costTypes = Object.keys(costSchedules);

      // Hitung annual summary (pengelompokan per tahun)
      const annualSummary = {};
      consolidated.forEach((row) => {
        const yr = row.year;
        if (!annualSummary[yr]) {
          annualSummary[yr] = {
            year: yr,
            total_invoice: 0,
            total_cash_in: 0,
            total_revenue_recognized: 0,
            total_deferred: 0,
          };
        }
        annualSummary[yr].total_invoice += row.total_invoice;
        annualSummary[yr].total_cash_in += row.total_cash_in;
        annualSummary[yr].total_revenue_recognized +=
          row.total_revenue_recognized;
        annualSummary[yr].total_deferred += row.total_deferred;
      });

      state.amortizationSchedule = consolidated;
      state.costSchedules = costSchedules;
      state.months = months;
      state.annualSummary = annualSummary;
      state.summary = {
        totalInvoice: consolidated.reduce((s, r) => s + r.total_invoice, 0),
        totalCashFlow: consolidated.reduce((s, r) => s + r.total_cash_in, 0),
        totalRevenue: consolidated.reduce(
          (s, r) => s + r.total_revenue_recognized,
          0,
        ),
        totalDeferred: consolidated.reduce(
          (s, r) => s + Math.abs(r.total_deferred),
          0,
        ),
        periodCount: months.length,
        unitCount: units.length,
        costTypes,
        startDate: months[0]?.label || "",
        endDate: months[months.length - 1]?.label || "",
        isRevenueShare,
        revenueType: isRevenueShare ? revenueType : null,
      };
    },

    // ── UI: filter per cost type ──────────────────────────────────────────────
    setSelectedCostType: (state, action) => {
      state.selectedCostType = action.payload;
    },

    // ── UI: view mode ─────────────────────────────────────────────────────────
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },

    // ── Set selected schedule row ─────────────────────────────────────────────
    setSelectedSchedule: (state, action) => {
      return { ...state, selectedSchedule: action.payload };
    },

    // ── Reset input body ──────────────────────────────────────────────────────
    setClearGenerateRevenueInputBody: (state) => {
      return {
        ...state,
        generateRevenueInputBody: initialState.generateRevenueInputBody,
      };
    },

    // ── Clear individual async states (mirip setClearCreateRole) ──────────────
    setClearGetRevenue: (state) => {
      return { ...state, getRevenue: asyncState() };
    },
    setClearGenerateRevenue: (state) => {
      return { ...state, generateRevenue: asyncState() };
    },
    setClearUpdateRevenue: (state) => {
      return { ...state, updateRevenue: asyncState() };
    },
    setClearDeleteRevenue: (state) => {
      return { ...state, deleteRevenue: asyncState() };
    },
    setClearExportRevenue: (state) => {
      return { ...state, exportRevenue: asyncState() };
    },

    // ── Clear semua computed schedule ─────────────────────────────────────────
    setClearSchedule: (state) => {
      return {
        ...state,
        amortizationSchedule: [],
        costSchedules: {},
        months: [],
        annualSummary: {},
        summary: initialState.summary,
        selectedCostType: "all",
        viewMode: "combined",
      };
    },

    // ── Clear semua state (mirip setClearAllRoleState) ────────────────────────
    setClearAllRevenueState: (state) => {
      return {
        ...state,
        getRevenue: asyncState(),
        generateRevenue: asyncState(),
        updateRevenue: asyncState(),
        deleteRevenue: asyncState(),
        exportRevenue: asyncState(),
        amortizationSchedule: [],
        costSchedules: {},
        months: [],
        summary: initialState.summary,
        selectedCostType: "all",
        viewMode: "combined",
        selectedSchedule: {},
      };
    },
  },

  // ─── Extra Reducers (async thunks) ──────────────────────────────────────────
  extraReducers(builder) {
    builder
      // ── GET REVENUE ──────────────────────────────────────────────────────────
      .addCase(getRevenueThunk.pending, (state) => ({
        ...state,
        getRevenue: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getRevenueThunk.fulfilled, (state, action) => ({
        ...state,
        getRevenue: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getRevenueThunk.rejected, (state, action) => ({
        ...state,
        getRevenue: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // ── GENERATE REVENUE ─────────────────────────────────────────────────────
      .addCase(generateRevenueThunk.pending, (state) => ({
        ...state,
        generateRevenue: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(generateRevenueThunk.fulfilled, (state, action) => ({
        ...state,
        generateRevenue: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(generateRevenueThunk.rejected, (state, action) => ({
        ...state,
        generateRevenue: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // ── UPDATE REVENUE ───────────────────────────────────────────────────────
      .addCase(updateRevenueThunk.pending, (state) => ({
        ...state,
        updateRevenue: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(updateRevenueThunk.fulfilled, (state, action) => ({
        ...state,
        updateRevenue: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(updateRevenueThunk.rejected, (state, action) => ({
        ...state,
        updateRevenue: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // ── DELETE REVENUE ───────────────────────────────────────────────────────
      .addCase(deleteRevenueThunk.pending, (state) => ({
        ...state,
        deleteRevenue: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(deleteRevenueThunk.fulfilled, (state, action) => ({
        ...state,
        deleteRevenue: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(deleteRevenueThunk.rejected, (state, action) => ({
        ...state,
        deleteRevenue: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      // ── EXPORT REVENUE ───────────────────────────────────────────────────────
      .addCase(exportRevenueThunk.pending, (state) => ({
        ...state,
        exportRevenue: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(exportRevenueThunk.fulfilled, (state, action) => ({
        ...state,
        exportRevenue: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(exportRevenueThunk.rejected, (state, action) => ({
        ...state,
        exportRevenue: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }));
  },
});

// ─── Export actions (mirip roleAction) ───────────────────────────────────────
export const revenueAction = {
  ...revenueSlice.actions,
  getRevenueThunk,
  generateRevenueThunk,
  updateRevenueThunk,
  deleteRevenueThunk,
  exportRevenueThunk,
};

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectRevenue = (state) => state?.revenue;
export const selectAmortizationSchedule = (state) =>
  state?.revenue?.amortizationSchedule;
export const selectCostSchedules = (state) => state?.revenue?.costSchedules;
export const selectRevenueSummary = (state) => state?.revenue?.summary;
export const selectAnnualSummary = (state) => state?.revenue?.annualSummary;
export const selectSelectedCostType = (state) =>
  state?.revenue?.selectedCostType;
export const selectViewMode = (state) => state?.revenue?.viewMode;
export const selectSelectedSchedule = (state) =>
  state?.revenue?.selectedSchedule;
export const selectGetRevenue = (state) => state?.revenue?.getRevenue;
export const selectGenerateRevenue = (state) => state?.revenue?.generateRevenue;

export default revenueSlice.reducer;
